import type { EventMarketDto, EventMarketLineDto, EventOddDto } from '@/api/queries';
import type { OddsFlash } from '@/ui/common/odds-button';
import { parseMarketsString } from './event-row';

export function outcomeFlashKey(
  eventId: number,
  marketKey: string,
  specifier: string | undefined,
  outcome: string,
): string {
  return `${eventId}:${marketKey}:${specifier ?? ''}:${outcome}`;
}

export function detectOddsDirection(prev: number, next: number): OddsFlash | undefined {
  if (!Number.isFinite(prev) || !Number.isFinite(next) || prev <= 0 || next <= 0) {
    return undefined;
  }
  const prevFixed = Number(prev.toFixed(2));
  const nextFixed = Number(next.toFixed(2));
  if (prevFixed === nextFixed) return undefined;
  return prevFixed > nextFixed ? 'down' : 'up';
}

function normalizeSpecifier(value: string | undefined | null): string {
  return value?.trim() ?? '';
}

function marketsMatch(existing: EventMarketDto, incoming: EventMarketDto): boolean {
  if (existing.Id !== undefined && incoming.Id !== undefined && existing.Id === incoming.Id) {
    return true;
  }
  if (existing.Key === incoming.Key) return true;
  if (incoming.Group === 'NXG' && existing.Group === 'NXG') return true;
  if (existing.Group === incoming.Group && existing.Key === incoming.Key) return true;
  return false;
}

function mergeOdd(existing: EventOddDto | undefined, incoming: EventOddDto): EventOddDto {
  return {
    Outcome: incoming.Outcome,
    Value: incoming.Value,
    Active: incoming.Active,
    Date: incoming.Date ?? existing?.Date ?? '',
  };
}

function mergeLine(existing: EventMarketLineDto | undefined, incoming: EventMarketLineDto): EventMarketLineDto {
  const existingOdds = existing?.Odds ?? [];
  const mergedOdds: EventOddDto[] = [];

  for (const incomingOdd of incoming.Odds ?? []) {
    const match = existingOdds.find((odd) => odd.Outcome === incomingOdd.Outcome);
    mergedOdds.push(mergeOdd(match, incomingOdd));
  }

  for (const existingOdd of existingOdds) {
    if (!mergedOdds.some((odd) => odd.Outcome === existingOdd.Outcome)) {
      mergedOdds.push(existingOdd);
    }
  }

  return {
    Specifier: incoming.Specifier ?? existing?.Specifier ?? '',
    Active: incoming.Active ?? existing?.Active,
    Main: incoming.Main ?? existing?.Main,
    Odds: mergedOdds,
  };
}

function mergeMarket(existing: EventMarketDto | undefined, incoming: EventMarketDto): EventMarketDto {
  if (!existing) {
    return {
      Id: incoming.Id,
      Key: incoming.Key,
      Group: incoming.Group ?? incoming.Key,
      Active: incoming.Active !== false,
      Lines: [...(incoming.Lines ?? [])],
    };
  }

  const existingLines = existing.Lines ?? [];
  const mergedLines: EventMarketLineDto[] = [];

  for (const incomingLine of incoming.Lines ?? []) {
    const specifier = normalizeSpecifier(incomingLine.Specifier);
    const match = existingLines.find(
      (line) => normalizeSpecifier(line.Specifier) === specifier,
    );
    mergedLines.push(mergeLine(match, incomingLine));
  }

  for (const existingLine of existingLines) {
    const specifier = normalizeSpecifier(existingLine.Specifier);
    if (!mergedLines.some((line) => normalizeSpecifier(line.Specifier) === specifier)) {
      mergedLines.push(existingLine);
    }
  }

  return {
    ...existing,
    Id: incoming.Id ?? existing.Id,
    Key: incoming.Key || existing.Key,
    Group: incoming.Group ?? existing.Group,
    Active: incoming.Active ?? existing.Active,
    Lines: mergedLines,
  };
}

/** Merge socket odds overlay into REST `MarketsString` snapshot. */
export function applyLiveOddsPatch(
  marketsString: string | null | undefined,
  incomingMarkets: EventMarketDto[] | null | undefined,
): string | null | undefined {
  if (!incomingMarkets?.length) return marketsString;

  const existing = parseMarketsString(marketsString) ?? [];
  const merged = [...existing];

  for (const incoming of incomingMarkets) {
    const index = merged.findIndex((market) => marketsMatch(market, incoming));
    if (index >= 0) {
      merged[index] = mergeMarket(merged[index], incoming);
    } else {
      merged.push(incoming);
    }
  }

  return JSON.stringify(merged);
}

/** Merge socket odds arrays (used by LiveFeedProvider cumulative patch store). */
export function mergeOddsPatches(
  existing: EventMarketDto[] | undefined,
  incoming: EventMarketDto[],
): EventMarketDto[] {
  const merged = [...(existing ?? [])];

  for (const incomingMarket of incoming) {
    const index = merged.findIndex((market) => marketsMatch(market, incomingMarket));
    if (index >= 0) {
      merged[index] = mergeMarket(merged[index], incomingMarket);
    } else {
      merged.push(incomingMarket);
    }
  }

  return merged;
}

/** Read outcome value from merged markets for flash comparison. */
export function readOutcomeValue(
  markets: EventMarketDto[] | undefined,
  marketKey: string,
  specifier: string | undefined,
  outcome: string,
): number | undefined {
  return readOutcomeOdd(markets, marketKey, specifier, outcome)?.value;
}

export type OutcomeOddRead = {
  value: number;
  active: boolean;
  marketActive: boolean;
  lineActive: boolean;
};

export type ReadOutcomeOddOptions = {
  /** When true, do not fall back to the first odd on the line. */
  exactOutcome?: boolean;
};

/** Read outcome value + active flags for betslip closed-odds checks. */
export function readOutcomeOdd(
  markets: EventMarketDto[] | undefined,
  marketKey: string,
  specifier: string | undefined,
  outcome: string,
  options?: ReadOutcomeOddOptions,
): OutcomeOddRead | undefined {
  if (!markets?.length) return undefined;

  const market =
    markets.find((item) => item.Key === marketKey) ??
    markets.find((item) => item.Group === marketKey);

  if (!market?.Lines?.length) return undefined;

  const normalizedSpecifier = normalizeSpecifier(specifier);
  const line =
    market.Lines.find((item) => normalizeSpecifier(item.Specifier) === normalizedSpecifier) ??
    market.Lines.find((item) => item.Main === true) ??
    market.Lines[0];

  if (!line) return undefined;

  const exact = line.Odds?.find((item) => item.Outcome === outcome);
  const matched = options?.exactOutcome ? exact : (exact ?? line.Odds?.[0]);

  if (!matched) return undefined;

  return {
    value: matched.Value,
    active: matched.Active !== false,
    marketActive: market.Active !== false,
    lineActive: line.Active !== false,
  };
}
